import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, MapPin, Briefcase } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Şirketler ve Onaylanmış Markalar | isbul.org"
        description="isbul.org'da ilan veren şirketler ve mavi tikli onaylanmış markalar. Güvenilir işverenleri keşfedin."
        keywords="şirketler, işverenler, onaylı markalar, kurumsal"
        canonical="/sirketler"
      />
      <Header />

      <div className="bg-gradient-to-b from-blue-50/50 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1f222d]">Şirketler</h1>
          <p className="text-slate-600 mt-2">Onaylanmış markaları <span className="text-[#1da1f2] font-semibold">mavi tik</span> ile tanıyın.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : companies.length === 0 ? (
          <p className="text-center text-slate-500 py-20">Henüz şirket bulunmuyor.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {companies.map((c) => (
              <div key={c.id} data-testid={`company-card-${c.id}`} onClick={() => navigate(`/sirket/${c.id}`)} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                    {c.logo_file_id ? <img src={fileUrl(c.logo_file_id)} alt={c.company_name} className="w-full h-full object-contain" /> : <Building2 className="text-blue-600" size={24} />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h3 className="font-semibold text-[#1f222d] truncate">{c.company_name}</h3>
                      {c.verified && <VerifiedBadge size={16} />}
                    </div>
                    {c.sector && <p className="text-xs text-slate-500 mt-0.5">{c.sector}</p>}
                  </div>
                </div>
                {c.about && <p className="text-sm text-slate-500 mt-4 line-clamp-2">{c.about}</p>}
                <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100 text-sm">
                  <span className="inline-flex items-center gap-1 text-slate-500"><MapPin size={14} /> {c.location || "—"}</span>
                  <span className="inline-flex items-center gap-1 text-blue-600 font-semibold">
                    <Briefcase size={14} /> {c.job_count} ilan →
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
